import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { firstValueFrom, Subject } from "rxjs";
import { IMergedStepsModel, IStepsModel } from "../../views-interfaces/steps-model.interface";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { AuthService } from "../auth";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { IStepsResponse } from "./steps-response.interface";

@Injectable()
export class StepsService {
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly authService = inject(AuthService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly http = inject(HttpClient);

    private dashboardId: string;
    private lastDashboardId: string;
    stepsLoadedEmitter = new Subject();
    stepsLoadedEvent$ = this.stepsLoadedEmitter.asObservable();
    stepsLoaded = false;
    private stepsCache: Map<string, Promise<IStepsModel[]>> = new Map<string, Promise<IStepsModel[]>>();

    static readonly statesIndex = {
        notStarted: 0,
        active: 1,
        completed: 2,
        all: 3,
    };

    constructor() {
        this.authService.fetchedUserInfoEvent$.subscribe(async () => {
            this.dashboardId = await this.dashboardsService.getSafeCurrentDashboardId();
        });

        this.dashboardsService.dashboardChangeEvent$.subscribe(async () => {
            this.stepsLoaded = false;
        });

        this.dashboardsService.currentDashboardChanged$.subscribe(async () => {
            this.dashboardId = this.dashboardsService.getCurrentDashboardId();
            if (this.stepsLoaded) {
                this.refresh();
            }
        });
    }

    refresh() {
        this.stepsLoaded = false;
        this.stepsCache = new Map<string, Promise<IStepsModel[]>>();
    }

    async loadSteps() {
        try {
            const result = await firstValueFrom(
                this.http.get<IStepsResponse>(this.getUrl(), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            return result.Steps;
        } catch (error) {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
    }

    async getStepsList() {
        this.dashboardId = await this.dashboardsService.getSafeCurrentDashboardId();
        if (!this.stepsCache.get(this.dashboardId)) {
            this.stepsCache.set(this.dashboardId, this.loadSteps());
        }
        if (this.lastDashboardId !== this.dashboardId) {
            this.stepsCache.get(this.dashboardId).then(() => {
                this.stepsLoaded = true;
                this.stepsLoadedEmitter.next(true);
            });
            this.lastDashboardId = this.dashboardId;
        }
        return this.stepsCache.get(this.dashboardId);
    }

    addStep(stepData: IStepsModel) {
        return this.http.post<{ Code: number; Step: IStepsModel }>(this.getUrl(), stepData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    updateStep(stepData: IStepsModel) {
        return this.http.put<{ Code: number; Steps: string[] }>(this.getUrl(), stepData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    deleteStep(stepId: string) {
        return this.http.delete<{ Code: number; Step: string }>(this.getUrl() + stepId);
    }

    private getUrl(path = "steps/") {
        return (
            this.appSettingsService.appSettings.StepsService_URL +
            this.appSettingsService.appSettings.StepsService_Version +
            path +
            this.dashboardId +
            "/"
        );
    }

    static compareSteps(a: IStepsModel | IMergedStepsModel, b: IStepsModel | IMergedStepsModel) {
        const aIndex = StepsService.statesIndex[a.State];
        const bIndex = StepsService.statesIndex[b.State];
        if (aIndex != bIndex) {
            return aIndex - bIndex;
        } else {
            return a.Order - b.Order;
        }
    }
}
